import React from 'react';
import { Bell, BellOff, Volume2, VolumeX, MessageSquare, ShoppingCart, ClipboardList, FileText, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import useNotifications from '../hooks/useNotifications';

const NotificationSettings = ({ onClose }) => {
  const {
    permission,
    settings,
    requestPermission,
    updateSettings,
    playSound,
    isSupported
  } = useNotifications();
  
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isCustomer = user?.role === 'customer';

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success('Notifications enabled! You will now receive alerts for important updates.');
    } else {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  const handleTestSound = () => {
    playSound();
    toast.info('Test notification sound played');
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-700">
          <BellOff className="w-5 h-5" />
          <span>Your browser does not support push notifications.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Permission Status */}
      <div className={`p-4 rounded-lg border ${
        permission === 'granted' ? 'bg-emerald-50 border-emerald-200' :
        permission === 'denied' ? 'bg-red-50 border-red-200' :
        'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {permission === 'granted' ? (
              <Bell className="w-6 h-6 text-emerald-600" />
            ) : (
              <BellOff className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <p className="font-medium text-slate-900">
                {permission === 'granted' ? 'Notifications Enabled' :
                 permission === 'denied' ? 'Notifications Blocked' :
                 'Notifications Not Enabled'}
              </p>
              <p className="text-sm text-slate-600">
                {permission === 'granted' ? 'You will receive alerts for important updates' :
                 permission === 'denied' ? 'Please enable notifications in your browser settings' :
                 'Enable to receive real-time alerts'}
              </p>
            </div>
          </div>
          {permission !== 'granted' && permission !== 'denied' && (
            <Button onClick={handleEnableNotifications} className="bg-emerald-600 hover:bg-emerald-700">
              Enable Notifications
            </Button>
          )}
        </div>
      </div>

      {/* Sound Settings */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Volume2 className="w-5 h-5" />
          Sound Settings
        </h3>
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            {settings.sound_enabled ? (
              <Volume2 className="w-5 h-5 text-emerald-600" />
            ) : (
              <VolumeX className="w-5 h-5 text-slate-400" />
            )}
            <div>
              <Label htmlFor="sound-toggle" className="text-sm font-medium">Notification Sound</Label>
              <p className="text-xs text-slate-500">Play sound when notification arrives</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleTestSound}
              disabled={!settings.sound_enabled}
            >
              Test Sound
            </Button>
            <Switch
              id="sound-toggle"
              checked={settings.sound_enabled}
              onCheckedChange={(checked) => updateSettings({ sound_enabled: checked })}
            />
          </div>
        </div>
      </div>

      {/* Notification Types */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notification Types
        </h3>
        <p className="text-sm text-slate-600">Choose which events trigger notifications</p>
        
        <div className="space-y-3">
          {/* Messages */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              <div>
                <Label htmlFor="messages-toggle" className="text-sm font-medium">Incoming Messages</Label>
                <p className="text-xs text-slate-500">Direct messages and announcements</p>
              </div>
            </div>
            <Switch
              id="messages-toggle"
              checked={settings.messages}
              onCheckedChange={(checked) => updateSettings({ messages: checked })}
              disabled={permission !== 'granted'}
            />
          </div>

          {/* Order Requests - Not for customers */}
          {!isCustomer && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-purple-500" />
                <div>
                  <Label htmlFor="orders-toggle" className="text-sm font-medium">Customer Order Requests</Label>
                  <p className="text-xs text-slate-500">New orders from customers</p>
                </div>
              </div>
              <Switch
                id="orders-toggle"
                checked={settings.order_requests}
                onCheckedChange={(checked) => updateSettings({ order_requests: checked })}
                disabled={permission !== 'granted'}
              />
            </div>
          )}

          {/* Order Notifications - Not for customers */}
          {!isCustomer && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-amber-500" />
                <div>
                  <Label htmlFor="order-notif-toggle" className="text-sm font-medium">Order Notifications to Farm</Label>
                  <p className="text-xs text-slate-500">Order status updates</p>
                </div>
              </div>
              <Switch
                id="order-notif-toggle"
                checked={settings.order_notifications}
                onCheckedChange={(checked) => updateSettings({ order_notifications: checked })}
                disabled={permission !== 'granted'}
              />
            </div>
          )}

          {/* Pickup Ready - Only for customers */}
          {isCustomer && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-emerald-500" />
                <div>
                  <Label htmlFor="pickup-toggle" className="text-sm font-medium">Pickup Ready</Label>
                  <p className="text-xs text-slate-500">When your order is ready for pickup</p>
                </div>
              </div>
              <Switch
                id="pickup-toggle"
                checked={settings.pickup_ready !== false}
                onCheckedChange={(checked) => updateSettings({ pickup_ready: checked })}
                disabled={permission !== 'granted'}
              />
            </div>
          )}

          {/* Forms - Not for customers */}
          {!isCustomer && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-emerald-500" />
                <div>
                  <Label htmlFor="forms-toggle" className="text-sm font-medium">Form Submissions</Label>
                  <p className="text-xs text-slate-500">New form submissions and updates</p>
                </div>
              </div>
              <Switch
                id="forms-toggle"
                checked={settings.forms}
                onCheckedChange={(checked) => updateSettings({ forms: checked })}
                disabled={permission !== 'granted'}
              />
            </div>
          )}
        </div>
      </div>

      {permission !== 'granted' && (
        <p className="text-sm text-slate-500 italic">
          * Enable notifications above to customize notification types
        </p>
      )}
    </div>
  );
};

export default NotificationSettings;
